import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Calculator, Snowflake, Mountain, Plus, Trash2,
  TrendingDown, DollarSign, Clock, Trophy
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const DebtPaydown = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  
  const [debts, setDebts] = useState([
    { name: "Mortgage 1", balance: 200000, interest_rate: 7, minimum_payment: 1500 },
    { name: "Car Loan", balance: 25000, interest_rate: 6, minimum_payment: 450 }
  ]);
  const [extraPayment, setExtraPayment] = useState(500);
  const [strategy, setStrategy] = useState("avalanche");

  const addDebt = () => {
    setDebts([...debts, { name: "", balance: 0, interest_rate: 0, minimum_payment: 0 }]);
  };

  const removeDebt = (index) => {
    setDebts(debts.filter((_, i) => i !== index));
  };

  const updateDebt = (index, field, value) => {
    const updated = [...debts];
    updated[index][field] = value;
    setDebts(updated);
    setResult(null);
  };

  const calculate = async () => {
    const validDebts = debts.filter(d => d.name && d.balance > 0);
    if (validDebts.length === 0) {
      toast.error("Please add at least one debt");
      return;
    }

    setCalculating(true);
    try {
      const response = await axios.post(`${API}/calculator/debt-paydown`, {
        debts: validDebts,
        extra_payment: extraPayment,
        strategy
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data.results);
      toast.success("Debt paydown calculated!");
    } catch (error) {
      toast.error("Failed to calculate");
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Prepare chart data
  const chartData = result?.monthly_progress?.filter((_, i) => i % 3 === 0).map(month => {
    const data = { month: month.month };
    month.debts.forEach(d => {
      data[d.name] = d.balance;
    });
    data.total = month.debts.reduce((sum, d) => sum + d.balance, 0);
    return data;
  }) || [];

  return (
    <div className="min-h-screen bg-slate-900" data-testid="debt-paydown-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">Debt Paydown Strategies</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              <Button 
              onClick={calculate}
              disabled={calculating}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              data-testid="calculate-debt-btn">
              {calculating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-slate-900 mr-2"></div>
              ) : (
                <Calculator className="h-4 w-4 mr-2" />
              )}
              Calculate
            </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="space-y-6">
            {/* Strategy Selection */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Paydown Strategy</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setStrategy("avalanche")}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    strategy === "avalanche" 
                      ? 'border-cyan-500 bg-cyan-500/10' 
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <Mountain className={`h-6 w-6 mb-2 ${strategy === "avalanche" ? 'text-cyan-500' : 'text-slate-500'}`} />
                  <div className="font-semibold text-white">Avalanche</div>
                  <div className="text-xs text-slate-400 mt-1">Highest interest first - saves most money</div>
                </button>
                <button
                  onClick={() => setStrategy("snowball")}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    strategy === "snowball" 
                      ? 'border-amber-500 bg-amber-500/10' 
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <Snowflake className={`h-6 w-6 mb-2 ${strategy === "snowball" ? 'text-amber-500' : 'text-slate-500'}`} />
                  <div className="font-semibold text-white">Snowball</div>
                  <div className="text-xs text-slate-400 mt-1">Lowest balance first - quick wins</div>
                </button>
              </div>
            </div>

            {/* Extra Payment */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-500" />
                Extra Monthly Payment
              </h3>
              <Input
                type="number"
                value={extraPayment}
                onChange={(e) => {
                  setExtraPayment(parseFloat(e.target.value) || 0);
                  setResult(null);
                }}
                className="bg-slate-950 border-slate-800 text-white font-mono text-xl"
                placeholder="500"
              />
              <p className="text-xs text-slate-500 mt-2">
                Extra amount above minimum payments to accelerate payoff
              </p>
            </div>

            {/* Debts */}
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Your Debts</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addDebt}
                  className="border-slate-700 text-slate-300"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Debt
                </Button>
              </div>
              <div className="space-y-4">
                {debts.map((debt, index) => (
                  <div key={index} className="p-4 bg-slate-950/50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <Input
                        value={debt.name}
                        onChange={(e) => updateDebt(index, 'name', e.target.value)}
                        placeholder="Debt name"
                        className="bg-transparent border-0 text-white font-semibold p-0 h-auto focus-visible:ring-0"
                      />
                      {debts.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDebt(index)}
                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Balance</Label>
                        <Input
                          type="number"
                          value={debt.balance}
                          onChange={(e) => updateDebt(index, 'balance', parseFloat(e.target.value) || 0)}
                          className="bg-slate-900 border-slate-800 text-white font-mono h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Rate %</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={debt.interest_rate}
                          onChange={(e) => updateDebt(index, 'interest_rate', parseFloat(e.target.value) || 0)}
                          className="bg-slate-900 border-slate-800 text-white font-mono h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Min Payment</Label>
                        <Input
                          type="number"
                          value={debt.minimum_payment}
                          onChange={(e) => updateDebt(index, 'minimum_payment', parseFloat(e.target.value) || 0)}
                          className="bg-slate-900 border-slate-800 text-white font-mono h-9"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {!result ? (
              <div className="glass-card rounded-xl p-12 text-center h-full flex flex-col items-center justify-center">
                <TrendingDown className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Debt Freedom Calculator</h3>
                <p className="text-slate-400 mb-6">See how fast you can pay off your debts</p>
                <div className="grid grid-cols-2 gap-4 text-sm text-left max-w-md">
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <Mountain className="h-4 w-4 text-cyan-500 mb-1" />
                    <div className="text-slate-300 font-medium">Avalanche</div>
                    <div className="text-slate-500 text-xs">Pay high interest first, save the most money</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3">
                    <Snowflake className="h-4 w-4 text-amber-500 mb-1" />
                    <div className="text-slate-300 font-medium">Snowball</div>
                    <div className="text-slate-500 text-xs">Pay smallest first, build momentum</div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Savings Summary */}
                <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Trophy className="h-8 w-8 text-emerald-500" />
                    <div>
                      <div className="text-sm text-slate-300">Interest Saved</div>
                      <div className="text-3xl font-bold font-mono text-emerald-500" data-testid="interest-saved">
                        {formatCurrency(result.comparison.interest_saved)}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-slate-400">
                    Pay off <span className="text-emerald-400 font-semibold">{result.comparison.months_saved} months faster</span> than minimum payments
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Debt-Free In</div>
                    <div className="text-2xl font-bold font-mono text-white">
                      {result.summary.payoff_years} years
                    </div>
                    <div className="text-xs text-slate-500">{result.summary.payoff_months} months</div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Total Interest</div>
                    <div className="text-2xl font-bold font-mono text-red-400">
                      {formatCurrency(result.summary.total_interest_paid)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Total Debt</div>
                    <div className="text-2xl font-bold font-mono text-white">
                      {formatCurrency(result.summary.total_debt)}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="text-sm text-slate-400 mb-1">Monthly Payment</div>
                    <div className="text-2xl font-bold font-mono text-amber-500">
                      {formatCurrency(result.summary.total_monthly_payment)}
                    </div>
                  </div>
                </div>

                {/* Comparison */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">vs. Minimum Payments Only</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-950/50 rounded-lg p-4">
                      <div className="text-sm text-slate-400 mb-1">With {result.strategy_name.split(' ')[0]}</div>
                      <div className="text-xl font-bold font-mono text-emerald-500">
                        {result.summary.payoff_years} years
                      </div>
                      <div className="text-xs text-slate-500">
                        {formatCurrency(result.summary.total_interest_paid)} interest
                      </div>
                    </div>
                    <div className="bg-slate-950/50 rounded-lg p-4">
                      <div className="text-sm text-slate-400 mb-1">Minimum Only</div>
                      <div className="text-xl font-bold font-mono text-red-400">
                        {result.comparison.min_payment_years} years
                      </div>
                      <div className="text-xs text-slate-500">
                        {formatCurrency(result.comparison.min_payment_interest)} interest
                      </div>
                    </div>
                  </div>
                </div>

                {/* Balance Chart */}
                {chartData.length > 0 && (
                  <div className="glass-card rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Debt Reduction Timeline</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData}>
                          <defs>
                            <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="month" stroke="#64748b" />
                          <YAxis stroke="#64748b" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                            formatter={(value) => formatCurrency(value)}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="total" 
                            stroke="#06b6d4" 
                            fill="url(#totalGrad)"
                            name="Total Balance"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Payoff Order */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-cyan-500" />
                    Payoff Order
                  </h3>
                  <div className="space-y-3">
                    {result.debt_payoff_order.map((debt, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-950/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            index === 0 ? 'bg-amber-500 text-slate-900' : 'bg-slate-700 text-slate-300'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-medium text-white">{debt.name}</div>
                            <div className="text-xs text-slate-500">{debt.interest_rate}% rate</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-mono text-white">{formatCurrency(debt.original_balance)}</div>
                          <div className="text-xs text-emerald-500">
                            Paid off month {debt.payoff_month}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DebtPaydown;
